import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, Archive, Loader2, Bot } from 'lucide-react';
import { base44 } from '@/api/base44Client';

export default function DocumentSaveDialog({ open, onOpenChange, documentOutput, onComplete }) {
  const [artifactCategory, setArtifactCategory] = useState('other');
  const [isSaving, setIsSaving] = useState(false);
  const [savedLink, setSavedLink] = useState(null);

  // Reset state when dialog opens/closes
  React.useEffect(() => {
    if (open) {
      setSavedLink(null);
      setIsSaving(false);
      setArtifactCategory('other');
    }
  }, [open]);

  const handleSave = async (createArtifact) => {
    if (!documentOutput) return;
    setIsSaving(true);
    
    try {
      const response = await base44.functions.invoke('saveBotDocument', {
        task_id: documentOutput.task_id,
        file_name: documentOutput.suggested_file_name,
        content: documentOutput.content,
        create_artifact: createArtifact,
        artifact_category: createArtifact ? artifactCategory : undefined
      });

      setSavedLink(response.data.drive_link);
      
      setTimeout(() => {
        setSavedLink(null);
        onComplete?.();
      }, 2000);
    } catch (error) {
      alert(`Failed to save document: ${error.response?.data?.error || error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="bots-documentsavedialog-dialog" className="bg-slate-900 border-slate-800 text-slate-100 max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bot className="w-5 h-5 text-purple-400" />
            Document Created
          </DialogTitle>
        </DialogHeader>
        
        {savedLink ? (
          <div className="py-6 text-center space-y-3">
            <div className="text-emerald-400 text-sm font-semibold">✅ Document saved successfully!</div>
            <a href={savedLink} target="_blank" rel="noopener noreferrer" className="text-sm text-indigo-400 hover:text-indigo-300 underline">
              Open in Google Drive
            </a>
          </div>
        ) : (
          <div className="space-y-4 mt-2">
            <p className="text-sm text-slate-400">
              The AI Officer created a document: <span className="text-slate-200 font-medium">{documentOutput?.suggested_file_name}</span>
            </p>
            <p className="text-sm text-slate-400">
              Would you like to save it to Google Drive and optionally create an artifact?
            </p>

            <Select value={artifactCategory} onValueChange={setArtifactCategory}>
              <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-100">
                <SelectValue placeholder="Artifact category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="funnel">Funnel</SelectItem>
                <SelectItem value="dashboard">Dashboard</SelectItem>
                <SelectItem value="protocol">Protocol</SelectItem>
                <SelectItem value="sop">SOP</SelectItem>
                <SelectItem value="template">Template</SelectItem>
                <SelectItem value="framework">Framework</SelectItem>
                <SelectItem value="checklist">Checklist</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>

            <div className="flex gap-3">
              <Button
                onClick={() => handleSave(false)}
                disabled={isSaving}
                variant="outline"
                className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800"
              >
                {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                Save to Drive Only
              </Button>
              <Button
                onClick={() => handleSave(true)}
                disabled={isSaving}
                className="flex-1 bg-cyan-600 hover:bg-cyan-700"
              >
                {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Archive className="w-4 h-4 mr-2" />}
                Save + Artifact
              </Button>
            </div>
            
            <Button
              onClick={() => onOpenChange(false)}
              variant="ghost"
              className="w-full text-xs text-slate-500 hover:text-slate-300"
            >
              Skip - don't save document
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}